import { useEffect, useState } from 'react';
import Spinner from '../../pages/design-detail/comps/spinner';

interface QuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => Promise<void> | void;
}

const QuantityModal = ({ isOpen, onClose, onConfirm }: QuantityModalProps) => {
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setQuantity('1');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const selectedQuantity = Number(quantity);

    if (!Number.isInteger(selectedQuantity) || selectedQuantity < 1) {
      setError('Enter a whole quantity of at least 1.');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await onConfirm(selectedQuantity);
      onClose();
    } catch (err) {
      console.error('Failed to add Liquid 3D design to cart:', err);
      setError('Unable to add this design to the cart. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Liquid 3D to Cart</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            aria-label="Close"
            className="text-2xl leading-none text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 px-6 py-5">
            <label htmlFor="liquid-3d-quantity" className="block text-sm font-medium text-gray-700">
              Quantity
            </label>
            <input
              id="liquid-3d-quantity"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={quantity}
              onChange={event => setQuantity(event.target.value)}
              disabled={processing}
              autoFocus
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="min-w-[108px] rounded-md bg-[#F36E29] px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {processing ? <Spinner spinning color="#fff" size={{ width: '20px', height: '20px' }} /> : 'Add to Cart'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuantityModal;